import React from 'react';
import {useIntl} from 'react-intl';
import {Route, Switch, useHistory, useLocation, useRouteMatch, Redirect} from 'react-router-dom';
import {SubNavigation} from 'web-components';
import Util from '../../forecast-app/shared/util/util';
import TimeApprovalPage, {TimeApprovalPageQuery} from './time-approval/TimeApprovalPage';
import Moment from 'moment';
import ForecastQueryRenderer from '../../ForecastQueryRenderer';
import TeamTimePage, {timesheetsTeamQuery} from './team-timesheet/timesheets_team';
import {createFragmentContainer} from 'react-relay';
import {graphql} from 'graphql';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {usingTimeApproval} from '../../forecast-app/shared/util/FeatureUtil';

export const TimeManagementPage = ({authFunc, viewer}) => {
	const LOCAL_STORAGE_KEY = 'time_approval_selected_tab';
	const {path} = useRouteMatch();
	const history = useHistory();
	const {pathname} = useLocation();
	const intl = useIntl();
	const companyTimesheetsAccess = Util.AuthorizeViewerAccess('team-time');
	const timeApprovalAccess = usingTimeApproval(viewer.company) && Util.AuthorizeViewerAccess('time-approval', viewer.projectOwner);
	const PAGES = {
		TIME_APPROVAL: 'time-approval',
		COMPANY_TIMESHEETS: 'company-timesheet',
	};

	const NAVIGATION_OPTIONS = [];
	if (timeApprovalAccess) {
		NAVIGATION_OPTIONS.push({
			value: PAGES.TIME_APPROVAL,
			label: intl.formatMessage({id: 'timesheet.time_approval'}),
		});
	}
	if (companyTimesheetsAccess) {
		NAVIGATION_OPTIONS.push({
			value: PAGES.COMPANY_TIMESHEETS,
			label: intl.formatMessage({id: 'timesheet.team_timesheet'}),
		});
	}

	const getActivePage = () => {
		let activePage = '';
		NAVIGATION_OPTIONS.forEach(option => {
			if (pathname.includes(option.value)) {
				activePage = option.value;
			}
		});
		return activePage;
	};

	const handleSetActivePage = value => {
		if (getActivePage() !== value && NAVIGATION_OPTIONS.some(option => option.value === value)) {
			history.push(value);
			Util.localStorageSetItem(LOCAL_STORAGE_KEY, value);
		}
	};

	const timeManagementName =
		timeApprovalAccess && companyTimesheetsAccess ? intl.formatMessage({id: 'timesheet.time_management'}) : timeApprovalAccess ? intl.formatMessage({id: 'timesheet.time_approval'}) : intl.formatMessage({id: 'timesheet.team_timesheet'});

	return (
		<>
			<TopHeaderBarWrapper sidePadding={24}>
				<TopHeaderBar title={timeManagementName} />
			</TopHeaderBarWrapper>
			{NAVIGATION_OPTIONS.length > 1 && (
				<div style={{margin: '20px 24px 0'}}>
					{NAVIGATION_OPTIONS.map(option => (
						<SubNavigation.NavigationItem
							active={getActivePage() === option.value}
							onClick={() => handleSetActivePage(option.value)}
							key={option.value}
							cy={'invoicing-tabs-' + option.value.toLowerCase()}
							userpilot={'invoicing-tabs-' + option.value.toLowerCase()}
						>
							{option.label}
						</SubNavigation.NavigationItem>
					))}
				</div>
			)}
			<Switch>
				<Route path={`${path}`} exact>
					<Redirect to={`${path}/${Util.localStorageGetItem(LOCAL_STORAGE_KEY) ? Util.localStorageGetItem(LOCAL_STORAGE_KEY) : PAGES.TIME_APPROVAL}`} />
				</Route>
				<Route exact path={[`${path}/${PAGES.TIME_APPROVAL}`, `${path}/${PAGES.TIME_APPROVAL}/T:taskId(\\d+)`]}>
					{timeApprovalAccess ? (
						<ForecastQueryRenderer
							key="query-render-timeApproval"
							componentName="time-approval"
							query={TimeApprovalPageQuery}
							showLoader={true}
							authorizeAccessRoute={'time-approval'}
							authorizeModule={authFunc}
							variables={{
								startDateString: Moment().clone().startOf('week').format('YYYY-MM-DD'),
								endDateString: Moment().clone().endOf('week').format('YYYY-MM-DD'),
								searchQuery: {filters: []},
							}}
							render={(relayProps, retry) => {
								return <TimeApprovalPage retry={retry} {...relayProps} />;
							}}
						/>
					) : (
						<Redirect to={`${path}/${PAGES.COMPANY_TIMESHEETS}`} />
					)}
				</Route>
				<Route exact path={[`${path}/${PAGES.COMPANY_TIMESHEETS}`, `${path}/${PAGES.COMPANY_TIMESHEETS}/T:taskId(\\d+)`]}>
					<ForecastQueryRenderer
						key="query-render-timesheetsTeam"
						componentName="team-timesheet"
						query={timesheetsTeamQuery}
						showLoader={true}
						variables={{
							startDateString: Moment().clone().startOf('week').format('YYYY-MM-DD'),
							endDateString: Moment().clone().endOf('week').format('YYYY-MM-DD'),
							searchQuery: localStorage.getItem(`team-timesheet-parsed-filters`) ? JSON.parse(localStorage.getItem(`team-timesheet-parsed-filters`)) : {filters: []},
						}}
						render={(relayProps, retry) => {
							return <TeamTimePage retry={retry} {...relayProps} />;
						}}
					/>
				</Route>
				<Route path={'*'}>
					<Redirect to={`${path}/${PAGES.TIME_APPROVAL}`} />
				</Route>
			</Switch>
		</>
	);
};

export const timeManagementQuery = graphql`
	query TimeManagementPage_Query {
		viewer {
			actualPersonId
			component(name: "time-management")
			company {
				useTimeApproval
			}
			...TimeManagementPage_viewer
		}
	}
`;

export default createFragmentContainer(TimeManagementPage, {
	viewer: graphql`
		fragment TimeManagementPage_viewer on Viewer {
			availableFeatureFlags {
				key
			}
			projectOwner
			company {
				useTimeApproval
				modules {
					moduleType
				}
			}
		}
	`,
});
